import { Outlet, createRootRouteWithContext } from '@tanstack/react-router'
import { useState, useEffect, lazy, Suspense } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { AuthModalProvider } from '@/contexts/AuthModalContext'
import { VerificationModalProvider } from '@/contexts/VerificationModalContext'
import { ToastProvider } from '@/components/shared/toast/Toast'
import { UserProvider } from '@/contexts/UserContext'
import { BottomNavigation } from '@/components/navigation/mobile/bottom-navigation/BottomNavigation'
import { BottomNavContext } from '@/contexts/BottomNavContext'
import { useMatchRoute } from '@tanstack/react-router'
import {
    HIDDEN_BOTTOM_NAV_URLS,
    DESKTOP_NAV_VISIBLE_URLS,
} from '@/constants/rootLayout'
import { classNames } from '@/utils/classNames'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary'
import { DesktopNavbar } from '@/components/navigation/desktop/navbar/Navbar'
import { cleanupStaleMarkers } from '@/utils/authFlowMarker'
import { AppUpdateManager } from '@/components/shared/AppUpdateManager'
import { useSafeAreaStore } from '@/stores/safeArea'

// Lazy load the shared product handler (only needed on native platforms)
const SharedProductHandler = lazy(() =>
    import('@/components/share/SharedProductHandler').then((module) => ({
        default: module.SharedProductHandler,
    }))
)

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
    {
        component: RootComponent,
        errorComponent: ErrorBoundary,
    }
)

function RootComponent() {
    const matchRoute = useMatchRoute()
    const showStatusBarCover = useSafeAreaStore((s) => s.showStatusBarCover)
    const statusBarColor = useSafeAreaStore((s) => s.statusBarColor)

    const hideBottomNav = HIDDEN_BOTTOM_NAV_URLS.some((to) =>
        matchRoute({ to })
    )
    const showBottomNav = !hideBottomNav

    // Desktop navigation logic: show on all routes except auth/onboarding,
    // plus override to show on specific editing routes even when mobile nav is hidden
    const hasDesktopNavOverride = DESKTOP_NAV_VISIBLE_URLS.some((to) =>
        matchRoute({ to })
    )
    const showDesktopNav = !hideBottomNav || hasDesktopNavOverride

    const baseNavHeight = 14 // Tailwind height unit (14 => h-14)
    const [bottomNavHeight, setBottomNavHeight] = useState(baseNavHeight)

    useEffect(() => {
        if (typeof navigator !== 'undefined') {
            const ua = navigator.userAgent || ''
            const isiOS =
                /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream
            if (isiOS) {
                setBottomNavHeight(baseNavHeight + 2)
            }
        }
    }, [])

    // Clean up stale markers after providers are initialized
    useEffect(() => {
        // Small delay to ensure router has stabilized after navigation
        const timer = setTimeout(() => {
            cleanupStaleMarkers()
        }, 100)

        return () => clearTimeout(timer)
    }, []) // Run once on mount

    const mainClassName = classNames()
    //     stickyHeader
    //         ? ''
    //         : lockScroll
    //           ? 'overflow-hidden'
    //           : 'overflow-y-auto overflow-x-hidden',
    //     showBottomNav ? `h-[calc(100dvh-${bottomNavHeight}rem)]` : 'h-[100dvh]',
    //     // Desktop: full height when sidebar is visible
    //     'md:h-[100dvh]'
    // )

    return (
        <BottomNavContext.Provider
            value={{
                isBottomNavVisible: showBottomNav,
                bottomNavHeight,
            }}
        >
            {/* PWA Update Manager - handles auto-reload on updates */}
            <AppUpdateManager />
            <ToastProvider>
                <UserProvider>
                    <AuthModalProvider>
                        <VerificationModalProvider>
                            {/* Handle shared product URLs from iOS Share Extension */}
                            <Suspense fallback={null}>
                                <SharedProductHandler />
                            </Suspense>

                            {/* Fixed cover for status bar area - prevents content scrolling behind notch */}
                            {/* Hidden on onboarding routes where video should go edge-to-edge */}
                            {/* Color changes to black during story viewer for better immersion */}
                            {showStatusBarCover && (
                                <div 
                                    className={`fixed left-0 right-0 top-0 z-[9999] h-safe-top md:hidden ${
                                        statusBarColor === 'black' ? 'bg-black' : 'bg-white'
                                    }`} 
                                />
                            )}

                            {/* Desktop Layout - body scrolling for proper Intersection Observer support */}
                            {/* flow-root creates a Block Formatting Context to contain child margins */}
                            <div className="hidden min-h-screen bg-[var(--color-bg)] md:flow-root">
                                {showDesktopNav && <DesktopNavbar />}
                                <div className="mt-header mx-auto">
                                    <main className={mainClassName}>
                                        <Outlet />
                                    </main>
                                </div>
                            </div>

                            {/* Mobile Layout */}
                            {/* pt-safe applied conditionally - disabled for onboarding routes */}
                            <div className={`md:hidden ${showStatusBarCover ? 'pt-safe' : ''}`}>
                                <div className="mx-auto max-w-md">
                                    <main className={mainClassName}>
                                        <Outlet />
                                    </main>
                                    {showBottomNav && <BottomNavigation />}
                                </div>
                            </div>
                        </VerificationModalProvider>
                    </AuthModalProvider>
                </UserProvider>
            </ToastProvider>
        </BottomNavContext.Provider>
    )
}
